import React from "react";
import { ReactSortable } from "react-sortablejs";
import CollapsibleSection from "./CollapsibleSection";
import Config from "../utils/Config";

function fileSig(file) {
    return `${file.name}::${file.size}::${file.lastModified}`;
}
function isImage(file) {
    return ["image/jpeg", "image/png", "image/webp"].includes(file.type);
}
function toPhotoObj(file) {
    return {
        id: fileSig(file),
        url: URL.createObjectURL(file),
        file,                 // present only for new files
        name: file.name,
        size: file.size,
        lastModified: file.lastModified,
        isNew: true,          // <-- mark NEW
    };
}

const MediaUploadPanel = ({
    isOpen,
    onToggle,
    logoFile,
    logoPreview,
    logoDropInputRef,
    bannerDropInputRef,
    fileInputRef,
    photos,
    formData,
    isDragOverLogo,
    isDragOverBanner,
    isDragOverPhoto,
    setLogoFile,
    setLogoPreview,
    setBannerCropSrc,
    setIsDragOverLogo,
    setIsDragOverBanner,
    setIsDragOverPhoto,
    setFormData,
    setPhotoCropSrc,
    setPhotoCropIndex,
    setPhotos,
    handleDroppedLogo,
    handleDroppedBanner,
    handleDroppedPhotos, // keep if you use it elsewhere; not needed in this version
    handleDeletePhoto,
    toast,
    displayWarning
}) => {
    const MAX = Config.APP.MAX_PHOTO_UPLOADS ?? 3;

    // Merge helper: valid images only, no duplicates, respect capacity.
    const addFiles = React.useCallback((fileList) => {
        const incoming = Array.from(fileList).filter(isImage);
        if (incoming.length === 0) return;

        setPhotos(prev => {
            const existingSigs = new Set(prev.map(p => p.id));
            const deduped = incoming.filter(f => !existingSigs.has(fileSig(f)));

            if (deduped.length === 0) return prev;

            const capacity = Math.max(0, MAX - prev.length);
            if (capacity <= 0) {
                displayWarning?.(`You can only upload up to ${MAX} photos.`);
                return prev;
            }

            const toTake = deduped.slice(0, capacity);
            const next = [...prev, ...toTake.map(toPhotoObj)];

            // Open cropper for the first newly added photo (if you use a crop flow)
            try {
                const first = toTake[0];
                if (first) {
                    const reader = new FileReader();
                    reader.onload = () => {
                        setPhotoCropSrc(reader.result);
                        setPhotoCropIndex(next.length - toTake.length); // index of the first new item
                    };
                    reader.readAsDataURL(first);
                }
            } catch { }

            if (deduped.length > capacity) {
                displayWarning?.(
                    `Only ${capacity} more photo${capacity === 1 ? "" : "s"} allowed (max ${MAX}).`
                );
            }
            return next;
        });
    }, [MAX, setPhotos, setPhotoCropSrc, setPhotoCropIndex, displayWarning]);

    return (
        <CollapsibleSection title="🔗 Media Upload" isOpen={isOpen} onToggle={onToggle}>
            {/* Logo Upload */}
            <div className="form-section">
                <h4 className="section-header-3">🏷️ Dealer Logo</h4>
                <small className="note">
                    Optional — square logo (PNG / JPG / WEBP, ≤512 KB). Will appear on search cards and in the microsite header.
                </small>

                <div className="file-upload-container">
                    <label className="custom-file-upload">
                        {logoFile ? "Update Logo" : "Upload Logo"}
                        <input
                            type="file"
                            accept="image/png,image/jpeg,image/webp"
                            onChange={(e) => {
                                const file = e.target.files?.[0];
                                if (!file) return;

                                if (file.size > 512 * 1024) {
                                    toast?.error("❌ Logo must be under 512 KB.");
                                    return;
                                }
                                if (!isImage(file)) {
                                    toast?.error("❌ Only PNG, JPG, or WEBP logos allowed.");
                                    return;
                                }

                                const previewUrl = URL.createObjectURL(file);
                                setLogoFile(file);
                                setLogoPreview(previewUrl);
                            }}
                        />
                    </label>
                    <span className="file-name">{logoFile ? logoFile.name : "No logo selected."}</span>
                </div>

                {logoPreview && (
                    <div className="photo-preview-grid">
                        <div className="photo-thumbnail-wrapper">
                            <img
                                src={logoPreview}
                                alt="Logo Preview"
                                className="photo-thumbnail"
                                onClick={() => setBannerCropSrc(logoPreview)}
                            />
                            <button
                                type="button"
                                className="delete-photo-button"
                                onClick={() => {
                                    setLogoFile(null);
                                    setLogoPreview(null);
                                }}
                            >
                                &times;
                            </button>
                        </div>
                    </div>
                )}

                <div
                    className={`photo-dropzone ${isDragOverLogo ? "drag-over" : ""}`}
                    onClick={() => logoDropInputRef.current?.click()}
                    onDragOver={(e) => {
                        e.preventDefault();
                        const item = e.dataTransfer.items?.[0];
                        if (item && item.kind === "file" && isImage({ type: item.type })) {
                            setIsDragOverLogo(true);
                        }
                    }}
                    onDragLeave={() => setIsDragOverLogo(false)}
                    onDrop={(e) => {
                        e.preventDefault();
                        const dropped = e.dataTransfer.files?.[0];
                        setIsDragOverLogo(false);
                        if (dropped) handleDroppedLogo?.(dropped);
                    }}
                >
                    <p>📅 Click or Drag & Drop logo here</p>
                </div>
                <input
                    type="file"
                    ref={logoDropInputRef}
                    style={{ display: "none" }}
                    accept="image/png,image/jpeg,image/webp"
                    onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) handleDroppedLogo?.(file);
                    }}
                />
            </div>

            {/* Banner Upload */}
            <div className="form-section">
                <h4 className="section-header-3">🖼️ Banner / Header Photo</h4>
                <small className="note">Optional – upload a wide banner (ideal: 1600×400) to brand your page.</small>

                <div className="file-upload-container">
                    <label className="custom-file-upload">
                        {formData.bannerFileName ? "Update Banner" : "Upload Banner"}
                        <input
                            type="file"
                            name="banner"
                            accept="image/jpeg,image/png,image/webp"
                            onChange={(e) => {
                                const selected = e.target.files?.[0];
                                if (!selected) return;
                                if (!isImage(selected)) {
                                    alert("❌ Only JPG, PNG, or WEBP files allowed.");
                                    return;
                                }
                                const reader = new FileReader();
                                reader.onload = () => setBannerCropSrc(reader.result);
                                reader.readAsDataURL(selected);
                            }}
                        />
                    </label>
                    <span className="file-name">{formData.bannerFileName || "No banner selected."}</span>
                </div>

                {formData.bannerPreview && (
                    <div className="photo-preview-grid">
                        <div className="photo-thumbnail-wrapper">
                            <img
                                src={formData.bannerPreview}
                                alt="Banner Preview"
                                className="photo-thumbnail"
                                onClick={() => setBannerCropSrc(formData.bannerPreview)}
                            />
                            <button
                                type="button"
                                className="delete-photo-button"
                                onClick={() =>
                                    setFormData((prev) => ({
                                        ...prev,
                                        bannerFile: null,
                                        bannerPreview: null,
                                        bannerFileName: null,
                                    }))
                                }
                            >
                                &times;
                            </button>
                        </div>
                    </div>
                )}

                <div
                    className={`photo-dropzone ${isDragOverBanner ? "drag-over" : ""}`}
                    onClick={() => bannerDropInputRef.current?.click()}
                    onDragOver={(e) => {
                        e.preventDefault();
                        const item = e.dataTransfer.items?.[0];
                        if (item && item.kind === "file" && isImage({ type: item.type })) {
                            setIsDragOverBanner(true);
                        }
                    }}
                    onDragLeave={() => setIsDragOverBanner(false)}
                    onDrop={(e) => {
                        e.preventDefault();
                        setIsDragOverBanner(false);
                        const dropped = e.dataTransfer.files?.[0];
                        if (dropped) handleDroppedBanner?.(dropped);
                    }}
                >
                    <p>📅 Click or Drag & Drop banner here</p>
                </div>
                <input
                    type="file"
                    ref={bannerDropInputRef}
                    style={{ display: "none" }}
                    accept="image/jpeg,image/png,image/webp"
                    onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) handleDroppedBanner?.(file);
                    }}
                />
            </div>

            {/* Photos Upload */}
            <div className="form-section">
                <h4 className="section-header-3">📷 Upload Photos</h4>
                <small className="note">Optional — Upload up to {MAX} photos: exterior, interior, optional</small>

                <div className="file-upload-container">
                    <label className="custom-file-upload">
                        {photos.length > 0 ? "Add Photos" : "Upload Photos"}
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/jpeg,image/png,image/webp"
                            multiple
                            onChange={(e) => {
                                const files = e.target.files;
                                if (!files || files.length === 0) return;
                                addFiles(files);
                                // reset input so selecting the same files again will still fire onChange
                                if (fileInputRef.current) fileInputRef.current.value = "";
                            }}
                        />
                    </label>
                    <span className="file-name">
                        {photos.length > 0 ? `${photos.length} photo(s) selected` : "No photos selected."}
                    </span>
                </div>

                <ReactSortable
                    list={photos}
                    setList={setPhotos}
                    className="photo-preview-grid"
                    animation={200}
                    handle=".photo-thumbnail"
                    ghostClass="drag-ghost"
                >
                    {photos.map((photo, index) => (
                        <div key={photo.id} className="photo-thumbnail-wrapper">
                            <img
                                src={photo.url}
                                alt={`Preview ${index + 1}`}
                                className="photo-thumbnail"
                                onClick={() => {
                                    setPhotoCropSrc(photo.url);
                                    setPhotoCropIndex(index);
                                }}
                            />
                            <button
                                type="button"
                                className="delete-photo-button"
                                onClick={() => {
                                    // revoke URL to avoid leaks
                                    try {
                                        if (photo?.url?.startsWith?.('blob:')) URL.revokeObjectURL(photo.url);
                                    } catch { }
                                    handleDeletePhoto(index);
                                }}
                            >
                                &times;
                            </button>
                        </div>
                    ))}
                </ReactSortable>

                <div
                    className={`photo-dropzone ${isDragOverPhoto ? "drag-over" : ""}`}
                    onClick={() => fileInputRef.current?.click()}
                    onDragOver={(e) => {
                        e.preventDefault();
                        const item = e.dataTransfer.items?.[0];
                        if (item && item.kind === "file" && isImage({ type: item.type })) {
                            setIsDragOverPhoto(true);
                        }
                    }}
                    onDragLeave={() => setIsDragOverPhoto(false)}
                    onDrop={(e) => {
                        e.preventDefault();
                        setIsDragOverPhoto(false);
                        const droppedFiles = Array.from(e.dataTransfer.files || []).filter(isImage);
                        if (droppedFiles.length) addFiles(droppedFiles);
                    }}
                >
                    <p>📅 Click or Drag & Drop photos here</p>
                </div>
            </div>
        </CollapsibleSection>
    );
};

export default MediaUploadPanel;